/**
 * Process Refund explanation templates keyed by refund reason.
 * Prefer Admin → Templates (order-canned-*) when loaded; these are fallbacks.
 */

export const REFUND_REASON_TEMPLATE_IDS = {
  shipping_refund: 'refund_shipping',
  requested_by_customer: 'refund_customer_request',
  duplicate: 'refund_duplicate',
  fraudulent: 'refund_fraudulent',
};

export const DEFAULT_REFUND_REASON_TEMPLATES = {
  refund_shipping: {
    id: 'refund_shipping',
    label: 'Refund Reason: Shipping',
    message: `Hello {customerName},

We're issuing a shipping refund of {refundAmount} for your order {orderNumber}.

This covers shipping and handling charges for this order. The refund will be returned to your original payment method and typically appears within 5-10 business days.

If you have any questions, please reply to this email.

Best regards,
CranfordPub Team`,
  },
  refund_customer_request: {
    id: 'refund_customer_request',
    label: 'Refund Reason: Customer Request',
    message: `Hello {customerName},

As requested, we've processed a refund of {refundAmount} for your order {orderNumber}.

The refund has been issued to your original payment method and should appear in your account within 5-10 business days, depending on your bank or card issuer.

Thank you for shopping with us. If you have any questions, please don't hesitate to contact us.

Best regards,
CranfordPub Team`,
  },
  refund_duplicate: {
    id: 'refund_duplicate',
    label: 'Refund Reason: Duplicate',
    message: `Hello {customerName},

We've identified a duplicate charge on your order {orderNumber} and have issued a refund of {refundAmount}.

The refund has been returned to your original payment method and should appear within 5-10 business days.

We apologize for any inconvenience this may have caused.

Best regards,
CranfordPub Team`,
  },
  refund_fraudulent: {
    id: 'refund_fraudulent',
    label: 'Refund Reason: Fraudulent',
    message: `Hello {customerName},

We've processed a refund of {refundAmount} for order {orderNumber}.

The funds have been returned to the original payment method and should appear within 5-10 business days.

If you did not place this order or have any concerns, please contact us right away.

Best regards,
CranfordPub Team`,
  },
};

export function fillRefundTemplate(template, { customerName, orderNumber, refundAmount } = {}) {
  return String(template || '')
    .replace(/\{customerName\}/g, customerName || 'Customer')
    .replace(/\{orderNumber\}/g, orderNumber || '')
    .replace(/\{refundAmount\}/g, refundAmount || '');
}

export function getRefundReasonTemplateMessage(reason, cannedMessages = []) {
  const templateId = REFUND_REASON_TEMPLATE_IDS[reason];
  if (!templateId) return '';

  const fromDb = Array.isArray(cannedMessages)
    ? cannedMessages.find((m) => m.id === templateId)
    : null;
  if (fromDb?.message) return fromDb.message;

  return DEFAULT_REFUND_REASON_TEMPLATES[templateId]?.message || '';
}

export function formatRefundAmountLabel(refundAmount) {
  const amountNum = parseFloat(refundAmount);
  return Number.isFinite(amountNum) ? `$${amountNum.toFixed(2)} CAD` : '';
}

export function buildRefundExplanationForReason(reason, order, refundAmount, cannedMessages = []) {
  const raw = getRefundReasonTemplateMessage(reason, cannedMessages);
  if (!raw) return '';

  return fillRefundTemplate(raw, {
    customerName: order?.customer_name || 'Customer',
    orderNumber: order?.order_number || '',
    refundAmount: formatRefundAmountLabel(refundAmount),
  });
}

/** Swap the previous amount label (or first $X.XX CAD) for the new amount. */
export function syncRefundAmountInExplanation(explanation, previousAmountLabel, nextAmount) {
  const nextLabel = formatRefundAmountLabel(nextAmount);
  if (!nextLabel || !explanation) return explanation || '';

  if (previousAmountLabel && explanation.includes(previousAmountLabel)) {
    return explanation.split(previousAmountLabel).join(nextLabel);
  }

  // Fallback: replace the first currency-looking amount in the email body
  const currencyMatch = explanation.match(/\$\d+(?:\.\d{1,2})?\s*CAD\b/);
  if (currencyMatch) {
    return explanation.replace(currencyMatch[0], nextLabel);
  }

  return explanation;
}
